import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import moment from "moment"
import DateRange from "@/components/partials/DateRange"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import filterImage from "@/assets/img/icons/filter.svg"

const year = moment().year()
const INITIAL_FILTERS = { start: year - 2, end: year + 4 }

function ClientGrowth() {
  const [loading, setLoading] = useState(true)
  const [labels, setLabels] = useState([])
  const [historical, setHistorical] = useState([])
  const [predicted, setPredicted] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)

        const f = `${filters.start ? `&start=${filters.start || ""}` : ""}${filters.end ? `&end=${filters.end || ""}` : ""}`


    Api.get(
      `analytics/client-growth?${f}`,
    )
      .then((res) => {
        setLabels(
          res.data.chart_data.labels?.map((date) => {
            return moment(date, "YYYY").isSame(moment(), "year") ? trans("analytics.today") : date
          }) || [],
        )
        setHistorical(res.data.chart_data.historical || [])
        setPredicted(res.data.chart_data.predicted || [])

        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const historicalSeries = labels.map((_, i) =>
    i < historical.length ? (historical[i] ?? null) : null,
  )

  const boundaryIndex = historical.length - 1
  const predictedSeries = labels.map((_, i) => {
    if (i === boundaryIndex) return historical[boundaryIndex] ?? null
    if (i > boundaryIndex) return predicted[i - historical.length] ?? null
    return null
  })

  function hasAnyData() {
    return labels.length > 0
  }

  function getAppliedFiltersCount() {
    return filters.start !== INITIAL_FILTERS.start || filters.end !== INITIAL_FILTERS.end ? 1 : 0
  }

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>{trans("analytics.clientGrowth")}</h3>
        <div className="right">
          <Button onClick={(e) => { compactFilterLayoutRef.current.open(e) }}>
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? <i>{getAppliedFiltersCount()}</i> : <></>}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className='row'><div className='col'>
              <DateRange
                startDate={filters.start}
                endDate={filters.end}
                onStartDateChange={(date) => {
                  setFilters((prev) => ({ ...prev, start: date }))
                }}
                onEndDateChange={(date) => {
                  setFilters((prev) => ({ ...prev, end: date }))
                }}
                yearOnly
              />
            </div></div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !hasAnyData() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <LineChart
          xAxis={[{ scaleType: "point", data: labels }]}
          series={[
            {
              id: "cg-hist",
              curve: "linear",
              label: trans("analytics.historicalData"),
              area: true,
              data: historicalSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
            {
              id: "cg-pred",
              curve: "linear",
              label: trans("analytics.forecast"),
              data: predictedSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
          ]}
          height={295}
          sx={{
            "& .MuiLineElement-series-cg-pred": { strokeDasharray: "6 3" },
            "& .MuiAreaElement-series-cg-hist": {
              fill: "url(#cg-hist-gradient)",
            },
            "& .MuiLineElement-root": { strokeWidth: 1 },
          }}
          slotProps={{
            legend: { hidden: true },
          }}
        >
          <defs>
            <linearGradient id="cg-hist-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#181F67" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </LineChart>
      )}
    </div>
  )
}

export default ClientGrowth
